import { BdGraphNode, BdGraph, BdGraphNodeStateMap, DoFunc } from './graph.model';

export namespace GraphUtils {
  
  // get node data if it exists, otherwise return null
  export function getNode<TNode extends BdGraphNode>(graph: BdGraph<TNode>, id: string): TNode {
    return graph.nodes[id] || null;
  }

  // get nearest ancestor if it exists, otherwise return null
  // this is a (private) utility 
  export function getAncestor<TNode extends BdGraphNode>(graph: BdGraph<TNode>, id: string): string {
    return graph.predecessors[id] || null;
  }


  // union of all immediate ancestors of a set of nodes
  export function getAllAncestorsFromList<TNode extends BdGraphNode>(graph: BdGraph<TNode>, targets: string[]): string[] {
    const ancestors = {};
    let _ancestor: string;
    targets.forEach((id) => {
      _ancestor = getAncestor(graph, id);
      if (_ancestor && !ancestors[_ancestor]) {
        ancestors[_ancestor] = true;
      }
    });
    return Object.keys(ancestors);
  }

  // return all child node ids of target node
  export function getSuccessors<TNode extends BdGraphNode>(graph: BdGraph<TNode>, id: string): string[] {
    return [...graph.successors[id] || []];
  }

  // get graph node state matching properties
  export function getState<TNode extends BdGraphNode>(graph: BdGraph<TNode>, id: string, properties: string[]): BdGraphNodeStateMap {
    const state = {};
    const node = getNode(graph, id);
    Object.entries(node.boolState).forEach(([property, value]) => {
      if (properties && properties.includes(property)) {
        state[property] = value;
      }
    });

    Object.entries(node.numState).forEach(([property, value]) => {
      if (properties && properties.includes(property)) {
        state[property] = value;
      }
    });

    return state;
  }

  // return true if node is a leaf node
  export function isLeaf<TNode extends BdGraphNode>(graph: BdGraph<TNode>, id: string): boolean {
    return !!graph.nodes[id] && graph.successors[id] === undefined;
  }


  // return true if node is a root node
  export function isRoot<TNode extends BdGraphNode>(graph: BdGraph<TNode>, id: string): boolean {
    return graph.roots.hasOwnProperty(id) || false;
  }

  // return all root-node ids
  export function getRoots<TNode extends BdGraphNode>(graph: BdGraph<TNode>): string[] {
    return Object.keys(graph.roots);
  }

  // recursively build graphNode from nested element
  function scrapeDataNode<TNode extends BdGraphNode>(graph: BdGraph<TNode>, parentId: string, nodeData: Object,
    idKey: string, childKey: string, props: Object, depth: number): void {

    // create graph node as specified generic type node
    const graphNode = new BdGraphNode(nodeData[idKey]) as TNode;

    // set Depth
    graphNode.setDepth(depth);

    // collect properties 
    Object.entries(props).forEach(([k, v]) => {
      let val: any;

      // get value (from string or function)
      if (typeof v === 'string') {
        val = nodeData[v];
      } else if (typeof v === 'function') {
        val = v(nodeData[idKey]);
      }

      // store as boolean or numerical state property
      if (typeof val === 'boolean') {
        graphNode.boolState[k] = val;
      } else if (typeof val === 'number') {
        graphNode.numState[k] = val;
      } else {
        graphNode[k] = val;
      }
    });

    // add node and relationships to graph
    graph.nodes[graphNode.id] = graphNode;
    if (parentId) {
      graph.successors[parentId] = [...graph.successors[parentId] || [], graphNode.id];
      graph.predecessors[graphNode.id] = parentId;
    } else {
      graph.roots[graphNode.id] = graphNode;
    }

    // recurse 
    depth += 1;
    nodeData[childKey].forEach((childData: Object) => {
      scrapeDataNode(graph, graphNode.id, childData, idKey, childKey, props, depth);
    });


  }


  // depth first traversal and do
  // this is a (private) utility 
  export function depthFirst<TNode extends BdGraphNode>(graph: BdGraph<TNode>, doFunc: DoFunc, id?: string): void {
    if (!id) {
      // start at the top fo the graph and operate on all roots
      getRoots(graph).forEach((rid) => {
        doFunc(rid);
        depthFirst(graph, doFunc, rid);
      });
    } else {
      // act on node and keep going down
      doFunc(id);
      getSuccessors(graph, id).forEach((cid) => {
        depthFirst(graph, doFunc, cid);
      });
    }
  }

  // factory for new BdGraph nested objects 
  const DEFAULT_ID_KEY = 'id';
  const DEFAULT_CHILDREN_KEY = 'children';
  export function createGraphFromSrc<TNode extends BdGraphNode>(
    srcData: Object[],
    idKey: string = DEFAULT_ID_KEY,
    childKey: string = DEFAULT_CHILDREN_KEY,
    nodeProperties: Object = {}
  ): BdGraph<TNode> {

    const depth = 0; // depth of root nodes is 0
    const graph = new BdGraph<TNode>();
    srcData.forEach((nodeData: Object) => {
      scrapeDataNode<TNode>(graph, null, nodeData, idKey, childKey, nodeProperties, depth);
    });

    return graph;
  }
}
